import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ScrollArea } from '../../components/ui/scroll-area';
import { toast } from 'sonner';
import api from '../../api';
import { 
  ClockCounterClockwise,
  MagnifyingGlass,
  Funnel,
  User,
  FileText,
  PencilSimple,
  Trash,
  Check,
  X,
  ArrowsClockwise,
  Download,
  CaretRight,
  Spinner
} from '@phosphor-icons/react';

export default function AuditPage() {
  const { isSuperAdmin } = useAuth();
  const [logs, setLogs] = useState([]);
  const [modules, setModules] = useState([]);
  const [actions, setActions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [filterModule, setFilterModule] = useState('all');
  const [filterAction, setFilterAction] = useState('all');
  const [searchUser, setSearchUser] = useState('');
  const [selectedRecord, setSelectedRecord] = useState(null);

  useEffect(() => {
    loadData();
  }, [filterModule, filterAction]);

  const loadData = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterModule !== 'all') params.module = filterModule;
      if (filterAction !== 'all') params.action = filterAction;
      
      const [logsRes, modulesRes, actionsRes, summaryRes] = await Promise.all([
        api.get('/audit/logs', { params }),
        api.get('/audit/modules'),
        api.get('/audit/actions'),
        api.get('/audit/summary', { params: { days: 7 } })
      ]);
      
      setLogs(logsRes.data);
      setModules(modulesRes.data);
      setActions(actionsRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      toast.error('Error al cargar auditoría');
    } finally {
      setLoading(false);
    }
  };

  const loadRecordHistory = async (recordId) => {
    try {
      const res = await api.get(`/audit/logs/record/${recordId}`);
      setSelectedRecord({ id: recordId, history: res.data });
    } catch (error) {
      toast.error('Error al cargar historial');
    }
  };

  const getActionIcon = (action) => {
    const icons = {
      crear: <Check size={14} className="text-green-600" />,
      editar: <PencilSimple size={14} className="text-blue-600" />,
      eliminar: <Trash size={14} className="text-red-600" />,
      activar: <Check size={14} className="text-green-600" />,
      desactivar: <X size={14} className="text-orange-600" />,
      asignar: <ArrowsClockwise size={14} className="text-purple-600" />,
      revocar: <X size={14} className="text-red-600" />,
      login: <User size={14} className="text-blue-600" />,
      logout: <User size={14} className="text-gray-600" />
    };
    return icons[action] || <FileText size={14} />;
  };

  const getActionColor = (action) => {
    const colors = {
      crear: 'bg-green-100 text-green-800',
      editar: 'bg-blue-100 text-blue-800',
      eliminar: 'bg-red-100 text-red-800',
      activar: 'bg-green-100 text-green-800',
      desactivar: 'bg-orange-100 text-orange-800',
      asignar: 'bg-purple-100 text-purple-800',
      revocar: 'bg-red-100 text-red-800',
      login: 'bg-blue-100 text-blue-800',
      logout: 'bg-gray-100 text-gray-800'
    };
    return colors[action] || 'bg-slate-100 text-slate-800';
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString('es-CL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredLogs = logs.filter(log => {
    if (searchUser) {
      const search = searchUser.toLowerCase();
      return log.user_email?.toLowerCase().includes(search) ||
             log.record_name?.toLowerCase().includes(search);
    }
    return true;
  });

  if (loading) {
    return (
      <>
        <div className="flex items-center justify-center h-64">
          <Spinner size={48} className="animate-spin text-blue-600" />
        </div>
      </>
    );
  }

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold font-['Chivo'] text-slate-900 flex items-center gap-3">
              <ClockCounterClockwise size={32} className="text-blue-600" />
              Auditoría del Sistema
            </h1>
            <p className="text-slate-500 mt-1">
              Registro de todas las acciones administrativas
            </p>
          </div>
          <Button variant="outline" onClick={loadData}>
            <ArrowsClockwise size={18} className="mr-2" />
            Actualizar
          </Button>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="text-3xl font-bold text-blue-600">{summary.total_actions}</div>
                <p className="text-sm text-slate-500">Acciones últimos {summary.period_days} días</p>
              </CardContent>
            </Card>
            {summary.by_module?.slice(0, 3).map((mod) => (
              <Card key={mod._id}>
                <CardContent className="pt-6">
                  <div className="text-2xl font-bold text-slate-700">{mod.total}</div>
                  <p className="text-sm text-slate-500 capitalize">{mod._id}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <MagnifyingGlass size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Buscar por usuario o registro..."
              value={searchUser}
              onChange={(e) => setSearchUser(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={filterModule} onValueChange={setFilterModule}>
            <SelectTrigger className="w-[200px]">
              <Funnel size={16} className="mr-2" />
              <SelectValue placeholder="Módulo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los módulos</SelectItem>
              {modules.map((m) => (
                <SelectItem key={m.id} value={m.id}>{m.nombre}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterAction} onValueChange={setFilterAction}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Acción" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las acciones</SelectItem>
              {actions.map((a) => (
                <SelectItem key={a.id} value={a.id}>{a.nombre}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Logs Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Registro de Actividad</CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[500px]">
              <div className="space-y-2">
                {filteredLogs.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    No hay registros de auditoría
                  </div>
                ) : (
                  filteredLogs.map((log) => (
                    <div 
                      key={log.id}
                      className="flex items-start gap-4 p-4 border rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                        {getActionIcon(log.action)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className={getActionColor(log.action)}>
                            {log.action}
                          </Badge>
                          <Badge variant="outline" className="capitalize">
                            {log.module}
                          </Badge>
                          {log.record_name && (
                            <span className="text-sm font-medium text-slate-700 truncate">
                              {log.record_name}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-600 mt-1">
                          {log.description}
                        </p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                          <span className="flex items-center gap-1">
                            <User size={12} />
                            {log.user_email}
                          </span>
                          <span>{formatDate(log.timestamp)}</span>
                          {log.ip_address && (
                            <span>IP: {log.ip_address}</span>
                          )}
                        </div>
                      </div>
                      {log.record_id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => loadRecordHistory(log.record_id)}
                          className="flex-shrink-0"
                        >
                          <CaretRight size={16} />
                        </Button>
                      )}
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Record History Modal */}
        {selectedRecord && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="w-full max-w-2xl max-h-[80vh] overflow-hidden">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Historial del Registro</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setSelectedRecord(null)}>
                  <X size={20} />
                </Button>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px]">
                  <div className="space-y-3">
                    {selectedRecord.history.map((entry, idx) => (
                      <div key={entry.id || idx} className="p-3 border rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className={getActionColor(entry.action)}>
                            {entry.action}
                          </Badge>
                          <span className="text-sm text-slate-500">
                            {formatDate(entry.timestamp)}
                          </span>
                        </div>
                        <p className="text-sm">{entry.description}</p>
                        <p className="text-xs text-slate-500 mt-1">
                          Por: {entry.user_email}
                        </p>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </>
  );
}
